import type { InvestableUniverseType, InvestmentActions, InvestmentStatus, RichAcl } from "@eu.mdotm.api/sphere";
import { useFunctionalAreas } from "$root/App/context";
import { runWithErrorReporting } from "$root/api/error-reporting";
import { buildUniverseInstrumentExportConfig } from "$root/functional-areas/instruments/export";
import { navigateToReviewIfAnythingToReviewSync } from "$root/functional-areas/instruments/review";
import { workspaceRepository } from "$root/functional-areas/workspace/repository";
import { useCrumbs } from "$root/components/Crumbs/useCrumbs";
import { LabelRounded } from "$root/components/LabelRounded/Index";
import { PageDownloadAndMoreActionsMenu } from "$root/components/PageDownloadAndMoreActionsMenu";
import { type PageHeaderProps, PageHeader } from "$root/components/PageHeader";
import { useTypedNavigation } from "$root/components/PlatformRouter/RoutesDef";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { useEventBus, useGroupEventBus } from "$root/event-bus";
import { spawnAccessDialog } from "$root/functional-areas/acl/AccessDialog";
import EntityStatus from "$root/functional-areas/acl/EntityStatus";
import { aclByArea } from "$root/functional-areas/acl/checkers/all";
import { SmallUniverseSummary } from "$root/functional-areas/universe/SmallUniverseSummary";
import { useUniverseEntityManagementActions } from "$root/functional-areas/universe/entity-management";
import { universeRepository } from "$root/functional-areas/universe/repository";
import { useUserValue } from "$root/functional-areas/user";
import { formatDate } from "$root/localization/formatters";
import { platformToast } from "$root/notification-system/toast";
import type { CustomAxiosError } from "$root/third-party-integrations/axios";
import { downloadContentDisposition, objMatchFn, seconds, ToastableError } from "$root/utils";
import { useQueryReactor } from "$root/third-party-integrations/react-query/hooks";
import { Button } from "@mdotm/shadcn/mdotm/components";
import { Icon } from "@mdotm/shadcn/legacy/components";
import { noop, unpromisify } from "@mdotm/shadcn/legacy/utils";
import type { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { ManualEditEntitiesEnum } from "../ManualCreation/common";
import { PortfolioStudioTab } from "../PortfoliosStudio/portfolio-studio-tabs";
import { ReviewUniverseCompositionCard } from "./partials/ReviewUniverseCompositionCard";
import { UniverseCompositionCard } from "./partials/UniverseCompositionCard";
import { ReviewEntity } from "../ReviewInstruments.const";

export function UniverseDetailsSubtitle(props: {
	universe?: {
		uuid?: string;
		name?: string;
		richAcl?: RichAcl;
		status?: InvestmentStatus;
		creationTime?: string;
		modificationTime?: string;
		action?: InvestmentActions;
		universeType?: InvestableUniverseType;
	};
}): React.JSX.Element {
	const { t } = useTranslation();
	const { universe } = props;
	const { richAcl, status } = universe ?? {};

	if (status === "REVIEW") {
		return <></>;
	}

	return (
		<div className="flex justify-between items-center text-[#585D68]">
			<div className="flex gap-4 items-center">
				<div
					className="flex items-center"
					data-qualifier="UniverseDetails/PageHeader/Status"
					data-status={universe?.status}
				>
					<span className="mr-2 uppercase">{t("STATUS")}</span>
					<LabelRounded content={{ status: universe?.status, component: "" }} />
				</div>
				<div>
					<span className="mr-2 uppercase">{t("INCEPTION_DATE")}:</span>
					{universe?.creationTime ? formatDate(universe.creationTime) : "..."}
				</div>
				<div>
					<span className="mr-2 uppercase">{t("PORTFOLIOS.PF_LAST_ACTION")}:</span>
					<span className="font-semibold text-[#616161]">{universe?.action}</span>
					&nbsp;
					{universe?.modificationTime ? <span>({formatDate(universe?.modificationTime)})</span> : "..."}
				</div>
				<EntityStatus
					accessControl={richAcl}
					entity={universe?.universeType === "GROUP_UNIVERSE" ? "GROUP_UNIVERSE" : "UNIVERSE"}
					entityId={universe?.uuid}
					entityName={universe?.name}
				/>
			</div>
		</div>
	);
}

export function UniverseDetailsTitleActions({
	universe,
	entityManagementActions,
	onEdit,
}: {
	universe?: {
		uuid?: string;
		name?: string;
		richAcl?: RichAcl;
		status?: InvestmentStatus;
		creationTime?: string;
		modificationTime?: string;
		action?: InvestmentActions;
		universeType?: InvestableUniverseType;
	};
	onEdit?: () => void;
	entityManagementActions: ReturnType<typeof useUniverseEntityManagementActions>;
}) {
	const { t } = useTranslation();
	const { richAcl } = universe ?? {};
	const user = useUserValue();
	const canEditComposition = aclByArea.portfolio.canEditComposition(user.id, richAcl?.acl ?? []);

	return (
		<div className="flex gap-2 items-center">
			{universe?.status === "READY" && canEditComposition && (
				<Button palette="primary" size="small" onClick={onEdit}>
					<Icon icon="Edit" size={16} />
					&nbsp;
					{t("BUTTON.EDIT")}
				</Button>
			)}

			<PageDownloadAndMoreActionsMenu
				area="universe"
				downloadActions={
					// TODO(MDOT-18838): remove once BE is ready, leave the other side of the ternary
					universe?.universeType === "GROUP_UNIVERSE"
						? undefined
						: universe?.status === "READY"
							? [
									{
										icon: "xls",
										disabled: !universe?.uuid,
										onClickAsync: async () => {
											await runWithErrorReporting(
												async () => {
													const uuid = universe.uuid;
													if (!uuid) {
														throw new ToastableError("Something went wrong", { icon: "Portfolio" });
													}

													await universeRepository.downloadUniverse(uuid);
												},
												{
													area: "universe",
													attemptedOperation: { message: "try to download universe template" },
												},
											);
										},
										label: "Universe template",
										"data-qualifier": "UniverseDetails/DropdownMenu/DropdownItem(UniverseTemplate)",
									},
									{
										icon: "xls",
										disabled: !universe?.uuid,
										onClickAsync: async () => {
											await runWithErrorReporting(
												async () => {
													const uuid = universe.uuid;
													if (!uuid) {
														throw new ToastableError("Something went wrong", { icon: "Portfolio" });
													}

													const config = buildUniverseInstrumentExportConfig(uuid);
													const file = await workspaceRepository.exportWorkspaceDataRaw.fetchQuery(config);
													await downloadContentDisposition(file);
												},
												{
													area: "universe",
													attemptedOperation: { message: "try to download universe composition" },
												},
											);
										},
										label: "Instrument list",
										"data-qualifier": "UniverseDetails/DropdownMenu/DropdownItem(InstrumentList)",
									},
								]
							: undefined
				}
				moreActions={[
					...(aclByArea.universe.canDelete(user.id, universe?.richAcl?.acl ?? [])
						? [
								{
									icon: "Delete" as const,
									"data-qualifier": "UniverseDetails/DropdownMenu/DropdownItem(Delete)",
									disabled: !entityManagementActions.deleteAsync,
									onClick: unpromisify(entityManagementActions.deleteAsync ?? noop),
									label: "Delete",
								},
							]
						: []),
					{
						icon: "Content-Copy" as const,
						"data-qualifier": "UniverseDetails/DropdownMenu/DropdownItem(Duplicate)",
						disabled: !entityManagementActions.duplicateAsync,
						onClick: unpromisify(entityManagementActions.duplicateAsync ?? noop),
						label: "Duplicate",
					} as const,
					...(entityManagementActions.renameAsync
						? [
								{
									icon: "Edit" as const,
									"data-qualifier": "UniverseDetails/DropdownMenu/DropdownItem(Rename)",
									onClick: unpromisify(entityManagementActions.renameAsync ?? noop),
									label: "Rename",
								},
							]
						: []),
				]}
			/>
		</div>
	);
}

interface IpageUrl {
	universeUuid: string;
}

const severityByStatus: Record<InvestmentStatus, PageHeaderProps["severity"]> = {
	ACCEPTED: "success",
	READY: "success",
	PROPOSAL_READY: "info",
	ERROR: "calculating", // Temporarily (MDOT-18271) mask Error status as Calculating until BE fixes their process
	CALCULATING: "calculating",
	REVIEW: undefined,
	RETRIEVING_DATA: "calculating",
	DRAFT: "info",
	EXECUTING: "calculating",
};

function UniverseDetails(): React.JSX.Element {
	const { universeUuid } = useParams<IpageUrl>();
	const { t } = useTranslation();
	const { push } = useTypedNavigation();

	const queryUniverseDetails = universeRepository.summary.useQuery(universeUuid);
	useQueryReactor(queryUniverseDetails, {
		onError: (error: AxiosError<CustomAxiosError>) => {
			if (error.response?.data.code === 404 || error.response?.data.message === "Accesso negato") {
				spawnAccessDialog({
					onClick: (onClose) => {
						push("PortfoliosStudio", { tab: PortfolioStudioTab.Universes });
						onClose();
					},
				});
				return;
			}

			platformToast({
				children: t("SOMETHING_WENT_WRONG"),
				severity: "error",
				icon: "Portfolio",
			});
			push("PortfoliosStudio", { status: "notFound", tab: PortfolioStudioTab.Universes });
		},
		onSuccess({ status }) {
			if (status !== "REVIEW") {
				return;
			}
			navigateToReviewIfAnythingToReviewSync(universeUuid, ReviewEntity.UNIVERSE, () =>
				push("ReviewInstruments", { entity: ReviewEntity.UNIVERSE, uuid: universeUuid }),
			);
		},
	});

	const { data: universe } = queryUniverseDetails;
	const { status, uuid, name } = universe ?? {};

	useFunctionalAreas(
		() => ({
			areas: "universe",
			data: { uuid: universeUuid, name },
		}),
		[universeUuid, name],
	);

	const entityManagementActions = useUniverseEntityManagementActions(universe, {
		onDuplicate: () => push("PortfoliosStudio", { tab: PortfolioStudioTab.Universes }),
		onDelete: () => push("PortfoliosStudio", { tab: PortfolioStudioTab.Universes }),
	});

	const crumbs = useCrumbs();

	useGroupEventBus("investment-update", () => queryUniverseDetails.refetch().catch(noop), {
		filter: objMatchFn({ uuid: universeUuid }),
		timeout: seconds(2),
	});

	// An import finishing is the one change to this universe that only the backend knows about, and
	// it has its own topic — `investment-update` never fires for it. Without this the composition an
	// import (UI- or bot-driven) just wrote stays hidden until the page is reloaded.
	useGroupEventBus("universe-import-update", () => queryUniverseDetails.refetch().catch(noop), {
		filter: objMatchFn({ uuid: universeUuid }),
		timeout: seconds(2),
	});

	useEventBus("shared-entity", {
		filter: objMatchFn({ sharedEntityUuid: universeUuid }),
		listener: () => {
			queryUniverseDetails.refetch().catch(noop);
		},
	});

	return (
		<>
			<PageHeader
				severity={severityByStatus[status!]}
				crumbs={[
					...crumbs.portfolioStudio("Universes", uuid),
					...crumbs.universeName({
						name,
						uuid,
					}),
				]}
				titleAction={
					<UniverseDetailsTitleActions
						entityManagementActions={entityManagementActions}
						universe={universe}
						onEdit={() =>
							push("Portfolios/ManualEdit", { entity: ManualEditEntitiesEnum.Universe, uuid: universe?.uuid ?? "" })
						}
					/>
				}
				subTitle={<UniverseDetailsSubtitle universe={universe} />}
			/>
			<ReactQueryWrapperBase query={queryUniverseDetails}>
				{(universeInner) => (
					<>
						<div className="mb-5">
							<SmallUniverseSummary universe={universeInner} />
						</div>
						{universeInner.status === "RETRIEVING_DATA" && <ReviewUniverseCompositionCard uuid={universeUuid} />}
						{universeInner.status !== "RETRIEVING_DATA" && universeInner.status !== "REVIEW" && (
							<UniverseCompositionCard universe={universeInner} />
						)}
					</>
				)}
			</ReactQueryWrapperBase>
		</>
	);
}

export default UniverseDetails;
